import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

const STORAGE_KEY = 'ab-onboarding:wizardSessionId';
const QUERY_KEY = 'sid';

/**
 * Stable wizard session identifier — generated on first wizard load,
 * persisted in `sessionStorage` so a hard refresh keeps the same ID for
 * the duration of the tab.
 *
 * Why
 * ---
 * Operators triaging a user-reported error mid-wizard need a stable
 * reference even when the user never reaches the success page. The
 * BE-generated `submissionId` is fresh per call (precheck / submit /
 * upload), so two calls within one wizard session don't share one. The
 * `wizardSessionId` survives the entire tab, gets passed to every BE
 * call as an extra field, and the BE logs it on every line — so the
 * user can quote a stable reference and ops can grep all logs / outbox
 * rows for that single id.
 *
 * Trust model
 * -----------
 * Client-generated UUID v4. NOT a security token. Not used for any
 * authorisation decision. The BE accepts whatever the client sends
 * (validated only as a 36-char string) and uses it solely as a log
 * correlation key.
 *
 * URL surfacing
 * -------------
 * The hook USED to auto-sync the resolved id back into the URL as
 * `?sid=<uuid>`. That had two failure modes that 2026-05-02 dev testing
 * exposed:
 *
 *   1. URL pollution — every fresh visit wrote a sid into the address
 *      bar even though the user never asked for one. Two consecutive
 *      fresh sessions both showed `?sid=<long-uuid>` and looked like
 *      "the same link came back" at a glance.
 *
 *   2. Race with the index `<Navigate to="organizzazione" replace />`
 *      under `RegistrationLayout`. React 18 fires children effects
 *      before parents, so the Navigate fired first; then the Layout's
 *      sid-sync effect ran with a STALE `location.pathname` captured at
 *      render time (= `/registrazione-intermediario`, before the
 *      Navigate took effect) and replaced the URL back to the wizard
 *      root, undoing the Navigate. The Navigate component's effect had
 *      already run (empty deps), so it didn't fire again — the user
 *      stayed on `/registrazione-intermediario?sid=…` with `<Outlet>`
 *      rendering nothing. Symptom: blank page.
 *
 * Both failure modes go away when the URL is left alone:
 *   - Sid still resolves correctly (URL > sessionStorage > fresh).
 *   - Sid still propagates to BE calls via `WizardSessionIdContext`.
 *   - URL stays clean unless an operator explicitly hands the user a
 *     deep URL with `?sid=<x>` (recovery flow), in which case the URL
 *     value is read on mount and persisted to sessionStorage.
 *
 * Behaviour
 * ---------
 * - First mount on any wizard route: read from URL, then sessionStorage,
 *   else generate a new one. Whichever wins, write back to sessionStorage.
 *   The URL is NEVER mutated by this hook.
 * - Page refresh: sessionStorage keeps the ID for the same tab.
 * - Tab close: drops the ID. A new wizard attempt is a new session.
 * - Direct visit to a deep URL with `?sid=<x>`: trusted (operator
 *   recovery URL, link reuse). The sid is read from URL, persisted in
 *   sessionStorage, and the URL is left as-is.
 */
export function useWizardSessionId(): string {
  const location = useLocation();

  // Resolve the ID once per mount. The URL is the source of truth on
  // first mount; subsequent navigations under the same layout reuse the
  // memoised value.
  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get(QUERY_KEY);
    if (fromUrl && isValidUuid(fromUrl)) {
      try {
        sessionStorage.setItem(STORAGE_KEY, fromUrl);
      } catch {
        // sessionStorage not available (private mode) — proceed without
      }
      return fromUrl;
    }
    let fromStorage: string | null = null;
    try {
      fromStorage = sessionStorage.getItem(STORAGE_KEY);
    } catch {
      fromStorage = null;
    }
    if (fromStorage && isValidUuid(fromStorage)) {
      return fromStorage;
    }
    const fresh = uuidv4();
    try {
      sessionStorage.setItem(STORAGE_KEY, fresh);
    } catch {
      // best-effort
    }
    return fresh;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: ID resolved on mount only, not on every location change
  }, []);
}

function isValidUuid(v: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    v,
  );
}
